import { Controller } from '@hotwired/stimulus';

/**
 * Loads a PDF via pdfjs-dist into a vertical stack of canvases (one per
 * page) inside `canvasContainer`. Lets the user click a page to drop pin
 * coordinates (in % of that page) and renders existing pins on each
 * page's pin layer. The visible page is tracked via IntersectionObserver
 * and surfaced through `pageIndicator`.
 *
 * Targets:
 * - canvasContainer — single scroll viewport that hosts the page stack.
 * - pageIndicator — text element ("N / total" of the most visible page).
 * - errorBanner — PDF.js failure banner.
 * - form, pageInput, xInput, yInput, commentInput — single new-annotation form.
 * - card — annotation cards (interaction surface for click-to-navigate).
 *
 * Per-page DOM is created at runtime; canvases and pin layers are NOT
 * Stimulus targets. Query them via `[data-page="N"]` inside
 * `canvasContainerTarget`.
 *
 * Values:
 * - fileUrl (String) — PDF URL (e.g. rails_storage_proxy_path).
 * - workerUrl (String) — bundled worker asset path (asset_path('pdf.worker.js')).
 * - readonly (Boolean) — disables click-to-pin.
 * - annotatableId (Number) — used to scope DOM ids (annotations-panel-{id}).
 */
export default class extends Controller {
  static targets = [
    'canvasContainer',
    'pageIndicator',
    'errorBanner',
    'form',
    'pageInput',
    'xInput',
    'yInput',
    'commentInput',
    'card',
  ];

  static values = {
    fileUrl: String,
    workerUrl: String,
    readonly: Boolean,
    annotatableId: Number,
  };

  async connect() {
    this.pdf = null;
    this.selectedId = null;
    this.draftPin = null;
    this.draftPinPage = null;
    this.visiblePage = 1;
    this.intersectionObserver = null;

    this.element.addEventListener('annotation:refresh', this.#onRefreshRequest);
    // Container-level click delegation — single listener survives every
    // re-render and gets GC'd by Stimulus disconnect via removeEventListener.
    if (!this.readonlyValue && this.hasCanvasContainerTarget) {
      this.canvasContainerTarget.addEventListener('click', this.#onCanvasClick);
    }

    // Listen for the host modal's `modal:open` event. The modal_controller
    // dispatches non-bubbling `modal:open` on its own element (so a
    // nested modal's open doesn't fire ancestor `lazy-iframe#load`-style
    // handlers), which means a Stimulus `modal:open@window` action would
    // never fire — descendants must listen on the modal element directly.
    this._modalEl = this.element.closest('[data-controller~="modal"]');
    if (this._modalEl) {
      this._modalEl.addEventListener('modal:open', this.#onRefreshRequest);
    }

    try {
      await this.#loadPdfjs();
      await this.#loadDocument();
      await this.#renderAll();
    } catch (error) {
      console.error('PDF.js load failed', error);
      this.#showError('Aperçu indisponible');
    }
  }

  disconnect() {
    this.element.removeEventListener('annotation:refresh', this.#onRefreshRequest);
    if (this.hasCanvasContainerTarget) {
      this.canvasContainerTarget.removeEventListener('click', this.#onCanvasClick);
    }
    if (this._modalEl) {
      this._modalEl.removeEventListener('modal:open', this.#onRefreshRequest);
      this._modalEl = null;
    }
    this.#disconnectIntersectionObserver();
  }

  cancelForm() {
    this.formTarget.classList.add('hidden');
    this.commentInputTarget.value = '';
    this.#clearDraftPin();
  }

  // Public action — bound from card click/keydown and from pin click.
  // We use an early-return guard rather than event.stopPropagation: the
  // delete affordance inside a card is itself a Stimulus-handled element
  // (confirmation-modal-trigger), and stopping bubble would risk masking
  // future descendant handlers we don't own. Checking event.target also
  // covers keyboard activation (Enter/Space on a focused inner button
  // dispatches a synthetic click that bubbles into us).
  select(event) {
    if (event.target.closest('a, button')) return;

    const el = event.currentTarget;
    const id = Number(el.dataset.annotationId);
    const page = Number(el.dataset.annotationPage);
    const yPct = Number(el.dataset.annotationY);
    if (!Number.isFinite(id) || !Number.isFinite(page)) return;

    this.selectedId = id;
    this.#applyCardStyles();
    this.#applyPinSelectionStyles();
    if (Number.isFinite(yPct)) {
      this.#scrollPinIntoView(page, yPct);
    }
    this.#scrollCardIntoView(id);
  }

  onFormSubmitEnd(event) {
    if (event.detail?.success) {
      this.cancelForm();
    }
  }

  /**
   * Re-render the entire page stack. Called from
   * - annotation_mode#enter via the `annotation:refresh` event because
   *   `connect()` runs while the controller root is `display:none`,
   *   making the canvas measure 0×0 and pins land at (0, 0). See Bug 2 in
   *   docs/superpowers/specs/2026-05-06-pdf-annotation-design.evidence/FIX_PLAN.md.
   * - annotation_panel#connect after every Turbo Stream replace of the
   *   annotations panel (create/destroy mutations).
   * - host modal's `modal:open` event (wired in `connect()` to the closest
   *   `[data-controller~="modal"]` ancestor) so the contractor read-only
   *   viewer modal renders into a real-size container after open. Direct
   *   listener (not `@window`) because modal_controller dispatches
   *   non-bubbling events so nested modals can't trigger ancestor handlers.
   */
  async refresh() {
    if (!this.pdf || !this.hasCanvasContainerTarget) return;
    // If canvases are already rendered at the correct fit-width, only
    // re-run #renderPins() — saves a 5s+ rebuild AND preserves the
    // user's scroll position when an annotation is created/destroyed via
    // Turbo Stream panel replace. Full rebuild is only needed when:
    //  - first render after connect (no canvases yet), or
    //  - modal:open transition where canvases were rendered at fallback
    //    1.5 scale because the container was hidden.
    const firstCanvas = this.canvasContainerTarget.querySelector('canvas[data-page="1"]');
    if (firstCanvas) {
      const canvasW = firstCanvas.getBoundingClientRect().width;
      const containerW = this.canvasContainerTarget.clientWidth;
      if (Math.abs(canvasW - containerW) < 1) {
        this.#renderPins();
        return;
      }
    }
    await this.#renderAll();
  }

  // ── private ──────────────────────────────────────────────

  async #loadPdfjs() {
    const pdfjs = await import('pdfjs-dist');
    pdfjs.GlobalWorkerOptions.workerSrc = this.workerUrlValue;
    this.pdfjs = pdfjs;
  }

  async #loadDocument() {
    this.pdf = await this.pdfjs.getDocument(this.fileUrlValue).promise;
    this.#updatePageIndicator();
  }

  // Tear down the existing stack, render every page, then place pins and
  // wire the IntersectionObserver. Cheap for invoice-sized PDFs (typical:
  // 1–4 pages). Re-entrant — every call rebuilds from scratch.
  async #renderAll() {
    if (!this.hasCanvasContainerTarget) return;
    const savedScrollTop = this.canvasContainerTarget.scrollTop;
    this.#disconnectIntersectionObserver();
    this.canvasContainerTarget.replaceChildren();
    this.draftPin = null;
    this.draftPinPage = null;

    const firstPage = await this.pdf.getPage(1);
    const scale = this.#computeFitWidthScale(firstPage);

    for (let pageNumber = 1; pageNumber <= this.pdf.numPages; pageNumber += 1) {
      await this.#renderOnePage(pageNumber, scale);
    }

    this.#renderPins();
    this.#observeVisiblePage();
    this.#updatePageIndicator();
    this.canvasContainerTarget.scrollTop = savedScrollTop;
  }

  async #renderOnePage(pageNumber, scale) {
    const page = await this.pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale });

    // Wrapper MUST be position: relative so the pin layer's `inset-0` is
    // scoped to this page, not to the scroll container.
    const wrapper = document.createElement('div');
    wrapper.dataset.page = String(pageNumber);
    wrapper.className =
      'relative mx-auto bg-white shadow-sm rounded-lg overflow-hidden mb-3 last:mb-0';
    wrapper.style.width = `${viewport.width}px`;
    wrapper.style.height = `${viewport.height}px`;

    const canvas = document.createElement('canvas');
    canvas.dataset.page = String(pageNumber);
    canvas.className = 'block';
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    if (!this.readonlyValue) canvas.style.cursor = 'crosshair';

    const pinLayer = document.createElement('div');
    pinLayer.dataset.page = String(pageNumber);
    pinLayer.dataset.role = 'pin-layer';
    pinLayer.className = 'absolute inset-0 pointer-events-none';

    wrapper.appendChild(canvas);
    wrapper.appendChild(pinLayer);
    this.canvasContainerTarget.appendChild(wrapper);

    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
  }

  // Fit-to-width: scale every page so page-1 matches the container's
  // current width. Falls back to 1.5 when the container is hidden
  // (clientWidth = 0) — the existing `annotation:refresh` event re-renders
  // when the parent becomes visible, so this fallback is only transient.
  #computeFitWidthScale(page) {
    const FALLBACK_SCALE = 1.5;
    if (!this.hasCanvasContainerTarget) return FALLBACK_SCALE;
    const containerWidth = this.canvasContainerTarget.clientWidth;
    if (containerWidth <= 0) return FALLBACK_SCALE;
    const naturalWidth = page.getViewport({ scale: 1 }).width;
    return containerWidth / naturalWidth;
  }

  #renderPins() {
    if (!this.hasCanvasContainerTarget) return;
    // Wipe every page's pin layer.
    this.canvasContainerTarget
      .querySelectorAll('[data-role="pin-layer"]')
      .forEach((layer) => layer.replaceChildren());

    const cards = document.querySelectorAll(
      `#annotations-panel-${this.annotatableIdValue} [data-annotation-id]`
    );

    cards.forEach((card, idx) => {
      const page = Number(card.dataset.annotationPage);
      const layer = this.#pinLayerFor(page);
      if (!layer) return;
      // Clamp visual placement to [2, 98] so pins at exact 0/100 boundaries
      // don't get half-clipped off-canvas (pin uses -translate-x/y-1/2).
      // The persisted coord is unchanged.
      const x = Math.min(98, Math.max(2, Number(card.dataset.annotationX)));
      const y = Math.min(98, Math.max(2, Number(card.dataset.annotationY)));
      const id = Number(card.dataset.annotationId);
      const isSelected = id === this.selectedId;
      const pin = document.createElement('div');
      const colorClasses = isSelected ? 'bg-warning-500 ring-4 ring-warning-200' : 'bg-danger-600';
      pin.className = `absolute -translate-x-1/2 -translate-y-1/2 w-10 h-10 md:w-7 md:h-7 rounded-full text-white text-xs font-semibold flex items-center justify-center shadow ${colorClasses}`;
      // % positioning works because the wrapper is sized exactly to the
      // canvas (viewport.width × viewport.height) and is position: relative.
      pin.style.left = `${x}%`;
      pin.style.top = `${y}%`;
      pin.textContent = String(idx + 1);
      pin.dataset.annotationId = String(id);
      pin.dataset.annotationPage = String(page);
      pin.style.pointerEvents = 'auto';
      pin.style.cursor = 'pointer';
      pin.addEventListener('click', this.#onPinClick);
      layer.appendChild(pin);
    });

    this.#redrawDraftPinIfActive();
  }

  // Update only the selected/unselected ring on existing pins without a
  // full #renderPins() — used by select() so we don't tear down the layer
  // mid-scroll. The DOM contract: pins carry data-annotation-id.
  #applyPinSelectionStyles() {
    if (!this.hasCanvasContainerTarget) return;
    this.canvasContainerTarget.querySelectorAll('[data-annotation-id]').forEach((pin) => {
      // Skip cards (which also have data-annotation-id) — pins are inside
      // a [data-role="pin-layer"] ancestor.
      if (!pin.closest('[data-role="pin-layer"]')) return;
      const isSelected = Number(pin.dataset.annotationId) === this.selectedId;
      pin.classList.toggle('bg-warning-500', isSelected);
      pin.classList.toggle('ring-4', isSelected);
      pin.classList.toggle('ring-warning-200', isSelected);
      pin.classList.toggle('bg-danger-600', !isSelected);
    });
  }

  // Redraw the draft pin after a re-render — needed on validation-failure
  // re-renders so the user keeps a visual anchor while fixing their
  // comment. Skipped if form is hidden.
  #redrawDraftPinIfActive() {
    if (!this.hasFormTarget || this.formTarget.classList.contains('hidden')) return;
    const formPage = Number(this.pageInputTarget.value);
    if (!formPage) return;
    const xPct = Number(this.xInputTarget.value);
    const yPct = Number(this.yInputTarget.value);
    if (Number.isNaN(xPct) || Number.isNaN(yPct)) return;
    this.#drawDraftPin(formPage, xPct, yPct);
  }

  #drawDraftPin(page, xPct, yPct) {
    const layer = this.#pinLayerFor(page);
    if (!layer) return;
    if (this.draftPin) this.draftPin.remove();
    const x = Math.min(98, Math.max(2, xPct));
    const y = Math.min(98, Math.max(2, yPct));
    const nextIndex =
      document.querySelectorAll(
        `#annotations-panel-${this.annotatableIdValue} [data-annotation-id]`
      ).length + 1;
    const pin = document.createElement('div');
    pin.dataset.draftPin = '';
    pin.className =
      'absolute -translate-x-1/2 -translate-y-1/2 w-10 h-10 md:w-7 md:h-7 rounded-full bg-danger-600 ring-2 ring-white/70 animate-pulse text-white text-xs font-semibold flex items-center justify-center shadow';
    pin.style.left = `${x}%`;
    pin.style.top = `${y}%`;
    pin.textContent = String(nextIndex);
    layer.appendChild(pin);
    this.draftPin = pin;
    this.draftPinPage = page;
  }

  #clearDraftPin() {
    if (this.draftPin) {
      this.draftPin.remove();
      this.draftPin = null;
      this.draftPinPage = null;
    }
  }

  #applyCardStyles() {
    if (!this.hasCardTarget) return;
    this.cardTargets.forEach((card) => {
      const isSelected = Number(card.dataset.annotationId) === this.selectedId;
      card.classList.toggle('border-warning-500', isSelected);
      card.classList.toggle('bg-warning-50', isSelected);
      card.classList.toggle('border-neutral-200', !isSelected);
      card.classList.toggle('bg-white', !isSelected);
      card.setAttribute('aria-pressed', String(isSelected));
    });
  }

  #scrollCardIntoView(id) {
    if (!this.hasCardTarget) return;
    const card = this.cardTargets.find((c) => Number(c.dataset.annotationId) === id);
    card?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  // Anchor the pin at ~1/3 of the visible viewport height so the user lands
  // on the actual annotation, not the top of its page. getBoundingClientRect
  // + scrollTop is used instead of offsetTop because the container isn't a
  // positioned ancestor (offsetParent could resolve outside it).
  #scrollPinIntoView(page, yPct) {
    const container = this.canvasContainerTarget;
    if (!container) return;
    const wrapper = Array.from(container.children).find(
      (child) => child.dataset.page === String(page)
    );
    if (!wrapper) return;

    const containerRect = container.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const pinScrollY =
      wrapperRect.top - containerRect.top + container.scrollTop + (yPct / 100) * wrapperRect.height;

    const targetTop = pinScrollY - container.clientHeight / 3;
    const maxScroll = container.scrollHeight - container.clientHeight;
    const clamped = Math.max(0, Math.min(targetTop, maxScroll));

    container.scrollTo({ top: clamped, behavior: 'smooth' });
  }

  #pinLayerFor(page) {
    return this.canvasContainerTarget?.querySelector(
      `[data-role="pin-layer"][data-page="${page}"]`
    );
  }

  #onPinClick = (event) => {
    // Pins delegate to the same selection method as cards. We do NOT
    // stopPropagation here — the canvas-click guard below filters by
    // event.target instead, which keeps any future delegated handlers
    // intact and keeps behaviour symmetric with the card path.
    this.select(event);
  };

  #onRefreshRequest = () => {
    this.refresh();
  };

  #showError(message) {
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.textContent = message;
    this.errorBannerTarget.classList.remove('hidden');
    if (this.hasCanvasContainerTarget) this.canvasContainerTarget.classList.add('hidden');
  }

  #updatePageIndicator() {
    if (!this.hasPageIndicatorTarget || !this.pdf) return;
    this.pageIndicatorTarget.textContent = `${this.visiblePage} / ${this.pdf.numPages}`;
  }

  // Container-level click delegation: we resolve the clicked page from
  // the closest [data-page] ancestor, ignoring clicks that bubbled from a
  // pin (pins set their own click handler) or from outside any page.
  #onCanvasClick = (event) => {
    if (this.readonlyValue) return;
    // A click on a pin is handled by #onPinClick — guard by checking
    // whether the event target is a pin or descendant of a pin layer.
    if (event.target.closest('[data-role="pin-layer"]')) return;

    const canvas = event.target.closest('canvas[data-page]');
    if (!canvas) return;

    const page = Number(canvas.dataset.page);
    if (!Number.isFinite(page)) return;

    const rect = canvas.getBoundingClientRect();
    const xPct = ((event.clientX - rect.left) / rect.width) * 100;
    const yPct = ((event.clientY - rect.top) / rect.height) * 100;

    this.pageInputTarget.value = page;
    this.xInputTarget.value = xPct.toFixed(2);
    this.yInputTarget.value = yPct.toFixed(2);
    this.#drawDraftPin(page, xPct, yPct);
    this.formTarget.classList.remove('hidden');
    this.commentInputTarget.focus();
  };

  // ── visible-page tracking ────────────────────────────────

  #observeVisiblePage() {
    if (!this.hasCanvasContainerTarget) return;
    if (typeof IntersectionObserver === 'undefined') return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the highest intersectionRatio above 0.1.
        // Track only WRAPPERS (direct children of the container) — they
        // carry data-page but no data-role.
        let best = null;
        entries.forEach((entry) => {
          if (entry.intersectionRatio < 0.1) return;
          if (!best || entry.intersectionRatio > best.intersectionRatio) {
            best = entry;
          }
        });
        if (!best) return;
        const page = Number(best.target.dataset.page);
        if (!Number.isFinite(page) || page === this.visiblePage) return;
        this.visiblePage = page;
        this.#updatePageIndicator();
      },
      {
        root: this.canvasContainerTarget,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    Array.from(this.canvasContainerTarget.children).forEach((wrapper) => {
      if (wrapper.dataset.page) this.intersectionObserver.observe(wrapper);
    });
  }

  #disconnectIntersectionObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }
}
